import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Toaster } from 'react-hot-toast';

import Landing from './Landing';
import LandingNew from './LandingNew';
import LiveChat from './LiveChat';
import PrivateRoute from './PrivateRoute';
import AccountManagement from './pages/AccountManagement';
import CompleteSignup from './pages/CompleteSignup';
import PaymentSuccess from './pages/PaymentSuccess';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import './PrivateRoute.css'; // Shared access page styling

/**
 * CallbackPage
 *
 * This page is shown during Auth0 callback processing.
 * The Auth0 SDK automatically handles the redirect and calls onRedirectCallback.
 * Also displays errors if Auth0 returns any (e.g., "signup is disabled")
 */
function CallbackPage() {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // Check for Auth0 errors in URL parameters
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('error');
    const authErrorDesc = params.get('error_description');

    if (authError) {
      setError({
        type: authError,
        description: authErrorDesc ? decodeURIComponent(authErrorDesc) : 'Er is een fout opgetreden'
      });
    }
  }, []);

  if (error) {
    return (
      <div className="access-page">
        <div className="access-card">
          <span className="access-icon">⚠️</span>
          <h1 className="access-title">Authenticatie Fout</h1>
          <p className="access-description" style={{
            padding: '16px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            border: '1px solid #fecaca',
            marginBottom: '24px'
          }}>
            <strong>Foutmelding:</strong> {error.description}
          </p>

          {error.type === 'invalid_request' && error.description.includes('signup') && (
            <div className="access-note" style={{ marginBottom: '24px', textAlign: 'left' }}>
              <p style={{ marginBottom: '8px' }}>
                <strong>💡 Mogelijke oplossing:</strong>
              </p>
              <p style={{ marginBottom: '8px' }}>
                Nieuwe registraties zijn momenteel uitgeschakeld. Start eerst een trial via de homepage.
              </p>
              <p>
                Heb je al een account? Gebruik dan de inlogknop.
              </p>
            </div>
          )}

          <div className="access-actions">
            <Link to="/" className="access-btn access-btn--primary">
              🏠 Terug naar Home
            </Link>
            <a
              href="mailto:support@humanaize.nl?subject=Auth0%20Login%20Error"
              className="access-btn access-btn--secondary"
            >
              📧 Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="access-loading">
      <span className="access-loading__icon">🔐</span>
      <p className="access-loading__text">Authenticatie voltooien...</p>
      <p className="access-loading__subtext">Je wordt zo doorgestuurd</p>
    </div>
  );
}

function App() {
  const { isLoading } = useAuth0();

  // Global loading state while Auth0 initializes
  if (isLoading) {
    return (
      <div className="access-loading">
        <span className="access-loading__icon">⏳</span>
        <p className="access-loading__text">Job laden...</p>
      </div>
    );
  }

  return (
    <SubscriptionProvider>
      {/* Toast notifications - placed at app level */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingNew />} />
        <Route path="/landing-old" element={<Landing />} />
        <Route path="/complete-signup" element={<CompleteSignup />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/callback" element={<CallbackPage />} />

        {/* Protected routes */}
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <LiveChat />
            </PrivateRoute>
          }
        />

        <Route
          path="/account"
          element={
            <PrivateRoute>
              <AccountManagement />
            </PrivateRoute>
          }
        />

        <Route
          path="/subscription/success"
          element={
            <PrivateRoute>
              <div style={{
                padding: '60px 40px',
                textAlign: 'center',
                maxWidth: '600px',
                margin: '0 auto',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '40px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '24px' }}>✅</div>
                  <h1 style={{ fontSize: '32px', marginBottom: '16px', color: '#1f2937' }}>
                    Welkom bij Job Personal Plan!
                  </h1>
                  <p style={{ fontSize: '18px', marginBottom: '32px', color: '#6b7280', lineHeight: '1.6' }}>
                    Je 7-daagse gratis proefperiode is gestart. We hebben je een bevestigingsmail gestuurd.
                  </p>
                  <Link
                    to="/app"
                    style={{
                      display: 'inline-block',
                      padding: '14px 32px',
                      backgroundColor: '#0d9488',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    Ga naar de app →
                  </Link>
                </div>
              </div>
            </PrivateRoute>
          }
        />

        <Route
          path="/subscription/cancel"
          element={
            <div className="access-page">
              <div className="access-card">
                <span className="access-icon">❌</span>
                <h1 className="access-title">Checkout Geannuleerd</h1>
                <p className="access-description">
                  Je hebt het checkout proces geannuleerd. Je kunt altijd later nog een abonnement starten.
                </p>
                <div className="access-actions">
                  <Link to="/app" className="access-btn access-btn--primary">
                    🚀 Terug naar de App
                  </Link>
                  <Link to="/" className="access-btn access-btn--secondary">
                    🏠 Naar Homepage
                  </Link>
                </div>
              </div>
            </div>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="access-page">
              <div className="access-card">
                <span className="access-icon">🔍</span>
                <h1 className="access-title">Pagina Niet Gevonden</h1>
                <p className="access-description">
                  Deze pagina bestaat niet of is verplaatst.
                </p>
                <div className="access-actions">
                  <Link to="/" className="access-btn access-btn--primary">
                    🏠 Terug naar Home
                  </Link>
                </div>
              </div>
            </div>
          }
        />
      </Routes>
    </SubscriptionProvider>
  );
}

export default App;
